import { Controller } from "@hotwired/stimulus"
export default class extends Controller {
  static targets = [
    "priceInput", "totalAmount", "totalInterest", "monthlyPayment",
    "durationSelect", "optionsList", "selectedValue", "durationInput",
     "shortText", "expandedText"
  ]

  static values = {
    baseUrl: String,
    color: String
  }

  connect() {
    this.setupClickOutside();
    this.calculateLoan();

    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideHandler);
  }

  disconnect() {
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  handleClickOutside(event) {
    if (!this.durationSelectTarget.contains(event.target)) {
      this.optionsListTarget.classList.add('hidden');
    }
  }

  setupClickOutside() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.optionsListTarget.classList.add('hidden');
      }
    });
  }

  toggleDurationSelect(e) {
    e.stopPropagation();
    this.optionsListTarget.classList.toggle('hidden');
  }

  selectDuration(e) {
    const value = e.target.getAttribute('data-value');
    const text = e.target.textContent;

    this.selectedValueTarget.textContent = text;
    this.durationInputTarget.value = value;
    this.optionsListTarget.classList.add('hidden');

    this.calculateLoan();
  }

  showExpandedText(e) {
    e.preventDefault();
    this.shortTextTarget.classList.add('hidden');
    this.expandedTextTarget.classList.remove('hidden');
  }

  onPriceChange(e) {
    let value = e.target.value.replace(/[^0-9]/g, '');

    if (!value) {
      value = '0';
    }
    value = value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    value = `${value} €`;
    const cursorPos = e.target.selectionStart;
    e.target.value = value;
    e.target.setSelectionRange(cursorPos, cursorPos);
    this.calculateLoan();
  }

  onBlur(e) {
    this.formatValue(e.target);
  }

  formatValue(input) {
    let value = input.value.replace(/[^0-9]/g, '');

    if (!value) {
      value = '0';
    }
    const formattedValue = `${value.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`;
    input.value = formattedValue;
    this.calculateLoan();
  }

  formatNumber(number) {
    let [integerPart, decimalPart = '00'] = number.toFixed(2).split('.');
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    decimalPart = decimalPart.padEnd(2, '0');
    return `${integerPart},${decimalPart}`;
  }

  formatNumberWithoutDecimal(number) {
    let numberStr = number.toString();
    let integerPart = numberStr.split('.')[0];
    return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  parsePrice(priceText) {
    return parseInt(priceText.replace(/[€\s.]/g, ''), 10);
  }

  async calculateLoan() {
    const price = this.parsePrice(this.priceInputTarget.value);
    const months = parseInt(this.durationInputTarget.value, 10);

    try {
      const response = await fetch(`/api/v1/finance/loan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({
          amount: price,
          num_installments: months
        })
      });

      const data = await response.json();
      this.updateCalculatorValues(data, price, months);
    } catch (error) {
      console.error('API Error:', error);
    }
  }

  updateCalculatorValues(data, price, months) {
    const tin = data.apr * 100;
    const tae = data.ear * 100;

    this.totalAmountTarget.textContent = `${this.formatNumber(data.total_payment_amount)} €`;
    this.totalInterestTarget.textContent = `${this.formatNumber(data.total_interest)} € (TAE ${this.formatNumber(tae)}%)`;
    this.monthlyPaymentTarget.textContent = `${this.formatNumber(data.installment)} €/mes`;

    this.shortTextTarget.innerHTML = `
      Ejemplo de financiación con Plettora para una cesta de ${this.formatNumberWithoutDecimal(price)} € a ${months - 1} meses y que comienza a pagar a los 30 días desde la solicitud.
      <span class="text-[#3A47BB] cursor-pointer hover:underline" data-action="click->loan-calculator#showExpandedText">ver más</span>
    `;

    this.expandedTextTarget.textContent = `
      Ejemplo de financiación con Plettora para una cesta de ${this.formatNumberWithoutDecimal(price)} € a ${months - 1} meses y que comienza a pagar a los 30 días desde la solicitud.
      Se solicita una entrada de ${this.formatNumber(data.installment)} € que el usuario debe pagar en el momento en que realiza la operación.
      El importe a financiar es la diferencia entre el valor de la cesta y la entrada: ${this.formatNumber(data.principal)} €.
      Se solicitan ${data.num_installments - 1} mensualidades de ${this.formatNumber(data.installment)} €.
      Comisión de apertura: 0 €. TIN: ${this.formatNumber(tin)}% TAE: ${this.formatNumber(tae)}%.
      Importe total adeudado: ${this.formatNumber(data.total_payment_amount)} €.
      Sujeto a la aprobación por parte de Plettora.
    `;
  }

  openCalculator() {
    window.open(`${this.baseUrlValue}/<%= @widget.widgetable.name %>`, '_blank');
  }
}
